import {I18nKeys} from '@multipass-i18n';
import {buttonClassName, Container, GlyphChip} from '@multipass-ui-components';
import {syncModelWithSelectorOptions, useSyncModelWithSelector} from 'esp-js-react';
import {GraduationCap} from 'lucide-react';
import * as React from 'react';

import {NAVIGATION_STORE_ID, NavigationStoreModel, Page, SESSION_STORE_ID, SessionStoreModel, ThemeService, useT} from '../../logic';
import {AppearanceMenu} from './AppearanceMenu';
import {LanguageMenu} from './LanguageMenu';
import {RouterLink} from './RouterLink';

export interface PortalHeaderProps {
    themeService: ThemeService;
}

const navAnchorClass = 'text-label font-semibold text-text-secondary no-underline';
const primaryCtaClass = buttonClassName();

export const PortalHeader: React.FunctionComponent<PortalHeaderProps> = ({themeService}: PortalHeaderProps) => {
    const t = useT();
    const currentPage = useSyncModelWithSelector<NavigationStoreModel, Page>(
        m => m.currentPage,
        syncModelWithSelectorOptions<Page>().setStoreId(NAVIGATION_STORE_ID)
    );
    const session = useSyncModelWithSelector<SessionStoreModel, SessionStoreModel>(
        m => m,
        syncModelWithSelectorOptions<SessionStoreModel>().setStoreId(SESSION_STORE_ID)
    );
    const isLoggedIn = session.status === 'loggedIn';
    const displayName = session.displayName ?? '';
    return (
        <header className="sticky top-0 z-sticky border-b border-border-subtle bg-surface">
            <Container className="flex items-center justify-between gap-4 py-3">
                <RouterLink to="/" className="flex items-center gap-3 font-display text-title-sm font-semibold text-text-primary no-underline">
                    <GlyphChip className="font-hanzi-ui">學</GlyphChip>
                    {t(I18nKeys.common.appName)}
                </RouterLink>
                <nav className="flex items-center gap-4">
                    {currentPage === 'landing' && (
                        <RouterLink to="/how-it-works" className={`${navAnchorClass} max-[680px]:hidden`}>{t(I18nKeys.header.howItWorks)}</RouterLink>
                    )}
                    {currentPage === 'howItWorks' && (
                        <RouterLink to="/" className={`${navAnchorClass} max-[680px]:hidden`}>{t(I18nKeys.header.home)}</RouterLink>
                    )}
                    <LanguageMenu/>
                    <AppearanceMenu themeService={themeService}/>
                    {isLoggedIn ? (
                        <>
                            <RouterLink
                                to="/home"
                                className="inline-flex min-h-[40px] items-center gap-2 rounded-pill border border-border-subtle bg-surface-muted px-3 text-text-primary no-underline"
                            >
                                <GraduationCap size={18} className="text-text-muted"/>
                                <span className="text-label font-semibold max-[440px]:hidden">{t(I18nKeys.header.studyHome)}</span>
                            </RouterLink>
                            <RouterLink
                                to="/account"
                                aria-label={t(I18nKeys.header.myAccount, {name: displayName})}
                                className="inline-flex min-h-[40px] items-center gap-2 rounded-pill border border-border-subtle bg-surface-muted py-[5px] pl-[5px] pr-2.5 text-text-primary no-underline"
                            >
                                <GlyphChip size="sm" shape="circle" className="text-label">{displayName.charAt(0).toUpperCase()}</GlyphChip>
                                <span className="text-label font-semibold max-[440px]:hidden">{displayName}</span>
                            </RouterLink>
                        </>
                    ) : (
                        <>
                            <RouterLink to="/login" className={navAnchorClass}>{t(I18nKeys.header.logIn)}</RouterLink>
                            <RouterLink to="/signup" className={primaryCtaClass}>{t(I18nKeys.common.startFree)}</RouterLink>
                        </>
                    )}
                </nav>
            </Container>
        </header>
    );
};
